import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import { Switch, TablePagination, Tooltip } from '@material-ui/core'
import { ReactComponent as BinIcon } from 'assets/img/bin.svg'
import { ReactComponent as DuplicateIcon } from 'assets/img/duplicate.svg'
import { ReactComponent as ArrowIcon } from 'assets/img/arrow-bottom.svg'
import {
  deleteTriggerRule,
  duplicateTriggerRule,
  fetchTriggerRule,
  fetchTriggerTableData,
  setTablePsfs,
  toggleTriggerRule,
} from 'reducers/jobsTriggers/reducer'
import {
  FIELD_TRIGGER_JOB_WORKFLOW,
  FIELD_TRIGGERED_JOB_WORKFLOW,
  FIELD_NAME,
  FIELD_TRIGGER_TYPE,
  FIELD_TRIGGER_ACTION,
  FIELD_SITE,
  FIELD_REPEAT,
  FIELD_ACTIVE,
  FIELD_ASSIGNMENT,
  VALUE_MONTHS,
  VALUE_WEEKS,
  VALUE_STOP,
} from 'constants/jobsTriggers'
import Psfs from 'lib/psfs/Psfs'
import CircleLoader from 'components/Loading/CircleLoader'
import withPromise from 'helpers/withPromise'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import { StyledIconWrap } from 'components/common/Inputs/styles'
import { Flex } from 'components/common/styles'
import messages from './translate'
import { StyledTable } from './styles'

const columns = [
  FIELD_NAME,
  FIELD_TRIGGER_JOB_WORKFLOW,
  FIELD_TRIGGER_TYPE,
  FIELD_TRIGGER_ACTION,
  FIELD_TRIGGERED_JOB_WORKFLOW,
  FIELD_SITE,
  FIELD_ASSIGNMENT,
  FIELD_REPEAT,
  FIELD_ACTIVE,
]

function ActionsColumn({
  id,
  deleteRule,
  duplicateRule,
  psfs,
  fetchTriggerTableData,
  isLoading,
  successfulMessage,
  failedMessage,
  formatMessage,
}) {
  const [dialog, setDialog] = useState(false)

  const onDuplicate = event => {
    event.stopPropagation()

    if (isLoading) {
      return
    }

    withPromise(duplicateRule, {
      id,
      successfulMessage,
      failedMessage,
    }).then(() => fetchTriggerTableData({ psfs }))
  }

  const onDelete = event => {
    event.stopPropagation()

    if (isLoading) {
      return
    }

    setDialog(true)
  }

  return (
    <td>
      <StyledIconWrap onClick={onDuplicate}>
        <Tooltip placement="top" title={formatMessage(messages.duplicate)}>
          <DuplicateIcon />
        </Tooltip>
      </StyledIconWrap>
      <StyledIconWrap onClick={onDelete}>
        <Tooltip placement="top" title={formatMessage(messages.delete)}>
          <BinIcon />
        </Tooltip>
      </StyledIconWrap>
      <ConfirmDialog
        open={dialog}
        title={formatMessage(messages.deleteTrigger)}
        className="confirm-action dialog-primary-custom"
        message={formatMessage(messages.deleteTriggerConfirmationMessage)}
        onConfirmAction={() => {
          setDialog(false)
          withPromise(deleteRule, id).then(() =>
            fetchTriggerTableData({ psfs }),
          )
        }}
        onClose={() => setDialog(false)}
        cancel={{
          color: 'default',
          variant: 'outlined',
        }}
      />
    </td>
  )
}

function JobsTriggersTable({
  triggerTableData,
  fetchTriggerTableData,
  deleteTriggerRule,
  toggleTriggerRule,
  intl: { formatMessage },
  setTablePsfs,
  tablePsfs,
  isLoading,
  isFetching,
  duplicateTriggerRule,
  setIsModalOpened,
  setActiveRuleId,
  fetchTriggerRule,
}) {
  const { data: tableData, count } = triggerTableData.toObject()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState(false)

  const changeSortColumn = index => {
    const { path } = tablePsfs
    const psfs = new Psfs(path)
    const order = index === sortColumn ? !sortDirection : false

    setSortColumn(index)
    setSortDirection(order)

    psfs.addSort(columns[index], order ? 'asc' : 'desc')

    setTablePsfs(psfs)
    fetchTriggerTableData({ psfs })
  }

  useEffect(() => {
    const { path } = tablePsfs
    const psfs = new Psfs(path)

    psfs.addPagination(page + 1, rowsPerPage)

    setTablePsfs(psfs)
    fetchTriggerTableData({ psfs })
  }, [page, rowsPerPage])

  const onChangeStatus = (active, id) => {
    if (isLoading) {
      return
    }

    withPromise(toggleTriggerRule, {
      active: !active,
      id,
      successfulMessage: formatMessage(messages.successfulTriggerUpdate),
      failedMessage: formatMessage(messages.failedTriggerUpdate),
    }).then(() => fetchTriggerTableData({ psfs: tablePsfs }))
  }

  const renderRepeat = (
    {
      frequency,
      repeatBy,
      dayOfWeek,
      weekOrdinalNumber,
      daysOfWeek,
      dayOfMonth,
    },
    triggerAction,
  ) => {
    if (repeatBy === VALUE_WEEKS && triggerAction !== VALUE_STOP) {
      return formatMessage(messages.triggerTableRepeatWeeks, {
        frequency,
        repeatBy,
        daysOfWeek: daysOfWeek?.toString(),
      })
    }

    if (repeatBy === VALUE_MONTHS && triggerAction !== VALUE_STOP) {
      if (dayOfWeek) {
        return formatMessage(messages.triggerTableRepeatMonthsWeek, {
          frequency,
          repeatBy,
          weekOrdinalNumber,
          dayOfWeek,
        })
      }

      return formatMessage(messages.triggerTableRepeatMonthsDay, {
        frequency,
        repeatBy,
        dayOfMonth,
      })
    }

    return formatMessage(messages.triggerTableRepeatDays, {
      frequency,
      repeatBy,
    })
  }

  return (
    <StyledTable direction="column">
      <div className="table-wrapper">
        <table>
          <thead>
            <tr className="table-row">
              <th onClick={() => changeSortColumn(0)}>
                <Flex align="center">
                  {formatMessage(messages.ruleName)}
                  {sortColumn === 0 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th onClick={() => changeSortColumn(1)}>
                <Flex align="center">
                  {formatMessage(messages.triggerJobTask)}
                  {sortColumn === 1 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th onClick={() => changeSortColumn(2)}>
                <Flex align="center">
                  {formatMessage(messages.triggerType)}
                  {sortColumn === 2 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th onClick={() => changeSortColumn(3)}>
                <Flex align="center">
                  {formatMessage(messages.triggerAction)}
                  {sortColumn === 3 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th onClick={() => changeSortColumn(4)}>
                <Flex align="center">
                  {formatMessage(messages.triggeredJob)}
                  {sortColumn === 4 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th onClick={() => changeSortColumn(5)}>
                <Flex align="center">
                  {formatMessage(messages.site)}
                  {sortColumn === 5 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th>
                <Flex align="center">{formatMessage(messages.assignment)}</Flex>
              </th>
              <th onClick={() => changeSortColumn(7)}>
                <Flex align="center">
                  {formatMessage(messages.repeat)}
                  {sortColumn === 7 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th onClick={() => changeSortColumn(8)}>
                <Flex align="center">
                  {formatMessage(messages.status)}
                  {sortColumn === 8 && (
                    <div className={cn({ arrow: sortDirection })}>
                      <ArrowIcon />
                    </div>
                  )}
                </Flex>
              </th>
              <th>{formatMessage(messages.actions)}</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map(
              ({
                active,
                assignment,
                id,
                name,
                repeat,
                site,
                sites,
                triggerAction,
                triggerJobWorkflow,
                triggerJobWorkflowTask,
                triggerType,
                triggeredJobWorkflow,
                regularRepeatSchedule,
              }) => (
                <tr
                  className="table-row"
                  key={id}
                  onClick={event => {
                    if (event.target.closest('tr')) {
                      setIsModalOpened(true)
                      setActiveRuleId(id)
                      fetchTriggerRule({ id })
                    }
                  }}>
                  <td>{name}</td>
                  <td>
                    {triggerJobWorkflow}
                    {triggerJobWorkflowTask
                      ? ` / ${triggerJobWorkflowTask}`
                      : ''}
                  </td>
                  <td>{triggerType}</td>
                  <td>{triggerAction}</td>
                  <td>{triggeredJobWorkflow}</td>
                  <td>{sites?.length ? sites.join(', ') : site}</td>
                  <td>{assignment}</td>
                  <td>
                    {Object.keys(regularRepeatSchedule).length
                      ? renderRepeat(regularRepeatSchedule, triggerAction)
                      : repeat}
                  </td>
                  <td className="status-col">
                    <Switch
                      checked={active}
                      onChange={() => {
                        onChangeStatus(active, id)
                      }}
                      onClick={event => {
                        event.stopPropagation()
                      }}
                      color="default"
                    />
                  </td>
                  <ActionsColumn
                    id={id}
                    deleteRule={deleteTriggerRule}
                    psfs={tablePsfs}
                    fetchTriggerTableData={fetchTriggerTableData}
                    isLoading={isLoading}
                    successfulMessage={formatMessage(
                      messages.successfulTriggerDuplicate,
                    )}
                    failedMessage={formatMessage(
                      messages.failedTriggerDuplicate,
                    )}
                    formatMessage={formatMessage}
                    duplicateRule={duplicateTriggerRule}
                  />
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {!isLoading && (
        <TablePagination
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onChangePage={(event, nextPage) => {
            setPage(nextPage)
          }}
          onChangeRowsPerPage={(event, { props: { value } }) => {
            setPage(0)
            setRowsPerPage(value)
          }}
          component="div"
          className="table-pagination"
          labelRowsPerPage={formatMessage({ id: 'rows_per_page' })}
          backIconButtonProps={{
            'aria-label': formatMessage({ id: 'previous_page' }),
          }}
          nextIconButtonProps={{
            'aria-label': formatMessage({ id: 'next_page' }),
          }}
        />
      )}
      <CircleLoader loading={isLoading || isFetching} />
    </StyledTable>
  )
}

export default connect(
  state => ({
    triggerTableData: state.getIn(['jobsTriggers', 'triggerTableData']),
    tablePsfs: state.getIn(['jobsTriggers', 'tablePsfs']),
    isLoading: state.getIn(['jobsTriggers', 'isLoading']),
    isFetching: state.getIn(['jobsTriggers', 'isFetching']),
  }),
  {
    fetchTriggerTableData,
    deleteTriggerRule,
    toggleTriggerRule,
    duplicateTriggerRule,
    setTablePsfs,
    fetchTriggerRule,
  },
)(injectIntl(JobsTriggersTable))
